<template>
  <nav class="navbar" :class="{ 'scrolled': isScrolled, 'menu-open': menuOpen }">
    <div class="nav-background"></div>
    <div class="nav-container">
      <router-link to="/" class="brand" @click="closeMenu">
        <span class="brand-mark">AS</span>
        <span class="brand-word">{{ $t('name') }}</span>
      </router-link>

      <ul class="nav">
        <li class="nav-item" v-for="(item, index) in navItems" :key="index">
          <router-link :to="item.path" class="nav-link">
            <span class="nav-text">{{ $t(item.label) }}</span>
            <span class="nav-indicator"></span>
          </router-link>
        </li>
      </ul>

      <router-link to="/contact" class="nav-cta">{{ $t('nav.cta') }}</router-link>

      <button
        class="nav-toggle"
        :class="{ active: menuOpen }"
        @click="menuOpen = !menuOpen"
        :aria-expanded="menuOpen"
        aria-label="Meni"
      >
        <span></span><span></span><span></span>
      </button>
    </div>

    <transition name="menu-fade">
      <div class="mobile-menu" v-if="menuOpen">
        <router-link
          v-for="(item, index) in navItems"
          :key="'m-' + index"
          :to="item.path"
          class="mobile-link"
          @click="closeMenu"
        >
          {{ $t(item.label) }}
        </router-link>
        <router-link to="/contact" class="mobile-cta" @click="closeMenu">{{ $t('nav.cta') }}</router-link>
      </div>
    </transition>
  </nav>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { ref, watch } from 'vue';
import { useRoute } from 'vue-router';

export default {
  name: 'NavBarComponents',
  setup() {
    const { t, locale } = useI18n();
    const isScrolled = ref(false);
    const menuOpen = ref(false);
    const route = useRoute();

    const navItems = [
      { path: '/', label: 'nav.about' },
      { path: '/galery', label: 'nav.gallery' },
      { path: '/contact', label: 'nav.contact' }
    ];

    const handleScroll = () => {
      isScrolled.value = window.scrollY > 20;
    };

    const closeMenu = () => {
      menuOpen.value = false;
    };

    watch(() => route.fullPath, closeMenu);
    watch(menuOpen, (open) => {
      document.body.style.overflow = open ? 'hidden' : '';
    });

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll);
    }

    return {
      t,
      locale,
      isScrolled,
      menuOpen,
      navItems,
      closeMenu
    };
  }
}
</script>

<style scoped>
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  padding: 22px 0;
  transition: padding 0.4s var(--ease);
}

.nav-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(250, 247, 242, 0.88);
  backdrop-filter: blur(14px) saturate(180%);
  -webkit-backdrop-filter: blur(14px) saturate(180%);
  border-bottom: 1px solid transparent;
  transition: all 0.4s var(--ease);
}

.navbar.scrolled,
.navbar.menu-open {
  padding: 14px 0;
}

.navbar.scrolled .nav-background,
.navbar.menu-open .nav-background {
  border-bottom-color: var(--color-border);
  box-shadow: 0 1px 30px rgba(14, 59, 67, 0.06);
}

.nav-container {
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 0 40px;
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 40px;
}

/* Brand */
.brand {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none !important;
  margin-right: auto;
}

.brand-mark {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--color-primary);
  color: #fff;
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.brand-word {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 18px;
  color: var(--color-primary);
  white-space: nowrap;
}

/* Desktop nav links */
.nav {
  display: flex;
  align-items: center;
  gap: 44px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.nav-item {
  position: relative;
}

.nav-link {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  padding: 8px 0;
  overflow: visible;
}

.nav-text {
  font-family: var(--font-body);
  color: var(--color-text);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  position: relative;
  transition: all 0.4s var(--ease);
}

.nav-indicator {
  position: absolute;
  bottom: -2px;
  left: 50%;
  width: 0;
  height: 1.5px;
  background: var(--color-accent);
  transform: translateX(-50%);
  transition: width 0.4s var(--ease);
}

.nav-link:hover .nav-text { color: var(--color-primary); }
.nav-link:hover .nav-indicator { width: 100%; }

.nav-link.router-link-active .nav-text { color: var(--color-primary); font-weight: 700; }
.nav-link.router-link-active .nav-indicator { width: 100%; }

/* CTA button */
.nav-cta {
  flex-shrink: 0;
  background: var(--color-primary);
  color: #fff !important;
  text-decoration: none !important;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.8px;
  padding: 12px 26px;
  border-radius: 30px;
  transition: all 0.35s var(--ease);
}

.nav-cta:hover {
  background: var(--color-primary-dark);
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(14, 59, 67, 0.25);
}

/* Mobile hamburger */
.nav-toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 34px;
  height: 34px;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  z-index: 3;
}

.nav-toggle span {
  display: block;
  width: 100%;
  height: 2px;
  background: var(--color-primary);
  border-radius: 2px;
  transition: all 0.35s var(--ease);
}

.nav-toggle.active span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
.nav-toggle.active span:nth-child(2) { opacity: 0; }
.nav-toggle.active span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

/* Mobile menu panel */
.mobile-menu {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  box-shadow: 0 20px 40px rgba(14, 59, 67, 0.1);
  display: flex;
  flex-direction: column;
  padding: 10px 30px 30px;
}

.mobile-link {
  padding: 16px 0;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text);
  text-decoration: none !important;
  font-family: var(--font-display);
  font-size: 20px;
  font-weight: 600;
}

.mobile-link.router-link-active { color: var(--color-primary); }

.mobile-cta {
  margin-top: 22px;
  text-align: center;
  background: var(--color-primary);
  color: #fff !important;
  text-decoration: none !important;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.8px;
  padding: 15px;
  border-radius: 30px;
}

.menu-fade-enter-active, .menu-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.menu-fade-enter-from, .menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Tablet */
@media (max-width: 1024px) {
  .nav-container { padding: 0 30px; gap: 30px; }
  .nav { gap: 30px; }
  .nav-text { font-size: 12px; }
}

/* Mobile: switch to hamburger */
@media (max-width: 860px) {
  .navbar { padding: 16px 0; }
  .navbar.scrolled { padding: 14px 0; }
  .nav-background { background: rgba(250, 247, 242, 0.96); }
  .nav, .nav-cta { display: none; }
  .nav-toggle { display: flex; }
  .brand-word { font-size: 16px; }
}

@media (max-width: 420px) {
  .nav-container { padding: 0 20px; }
  .brand-word { display: none; }
}

@media (prefers-reduced-motion: reduce) {
  .navbar, .nav-background, .nav-text, .nav-indicator, .nav-toggle span {
    transition: none;
  }
}
</style>
